import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Lock, Download, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const backgroundImage = "https://images.unsplash.com/photo-1760385737098-0b555a75b2ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwYXJjaGl0ZWN0dXJhbCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc3NjE0MTA1Nnww&ixlib=rb-4.1.0&q=85";

const ClientAccessPage = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [booking, setBooking] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      toast.error('Please enter your confirmation code');
      return;
    }

    setLoading(true);
    
    try {
      const { data } = await axios.post(`${API}/api/client/access`, {
        confirmation_code: code.toUpperCase()
      });
      
      if (data.valid) {
        setAccessGranted(true);
        setBooking(data.booking);
        setPhotos(data.photos.map(p => ({
          ...p,
          image_url: `${API}${p.image_url}`
        })));
        toast.success('Access granted! Welcome to your gallery.');
      }
    } catch (e) {
      console.error('Access error:', e);
      toast.error(e.response?.data?.detail || 'Invalid confirmation code');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (photo) => {
    try {
      const response = await fetch(photo.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = photo.original_filename || `photo-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Download started!');
    } catch (e) {
      console.error('Download error:', e);
      toast.error('Failed to download image');
    }
  };

  if (accessGranted) {
    return (
      <div className="min-h-screen bg-black pt-24" data-testid="client-gallery">
        {/* Header */}
        <section className="py-12 text-center">
          <div className="container-luxury">
            <p className="text-[#C9A14A] uppercase tracking-[0.2em] text-sm mb-4">Welcome Back</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-light text-white mb-4">
              Your Gallery
            </h1>
            {booking && (
              <div className="text-gray-400">
                <p>{booking.name} | {booking.session_type}</p>
                <p className="text-sm">{booking.date} at {booking.time}</p>
              </div>
            )}
          </div>
        </section>

        {/* Photos Grid */}
        <section className="pb-24">
          <div className="container-luxury">
            {photos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {photos.map((photo, index) => (
                  <div 
                    key={photo.id || index}
                    className="group relative aspect-[4/5] overflow-hidden cursor-pointer"
                    data-testid={`client-photo-${index}`}
                  >
                    <img
                      src={photo.image_url}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      onClick={() => setSelectedImage(photo)}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => setSelectedImage(photo)}
                        className="p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                      >
                        <ImageIcon className="w-5 h-5 text-white" />
                      </button>
                      <button
                        onClick={() => handleDownload(photo)}
                        className="p-3 bg-[#C9A14A] hover:bg-white rounded-full transition-colors"
                        data-testid={`download-photo-${index}`}
                      >
                        <Download className="w-5 h-5 text-black" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-2">No photos available yet</p>
                <p className="text-gray-500 text-sm">Your photos will appear here after your session.</p>
              </div>
            )}
          </div>
        </section>

        {/* Lightbox */}
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-5xl bg-black/95 border-[#333] p-0 overflow-hidden">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/80 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            {selectedImage && (
              <div className="relative">
                <img
                  src={selectedImage.image_url}
                  alt="Gallery preview"
                  className="w-full h-auto max-h-[85vh] object-contain"
                />
                <div className="absolute bottom-4 right-4">
                  <Button
                    onClick={() => handleDownload(selectedImage)}
                    className="btn-gold flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 relative" data-testid="client-access-page">
      {/* Background */}
      <div className="absolute inset-0">
        <img 
          src={backgroundImage} 
          alt="Background" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
      </div>

      {/* Access Card */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-6rem)] px-4">
        <div className="w-full max-w-md">
          <div className="card-luxury p-8 md:p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-[#C9A14A]/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#C9A14A]" />
            </div>

            <h1 className="font-['Playfair_Display'] text-3xl text-white mb-2">
              Access Your Photos
            </h1>
            <p className="text-gray-400 text-sm mb-8">
              Enter your confirmation code to unlock your private gallery
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="LVL-XXXX"
                  className="text-center text-xl tracking-widest bg-transparent border-b-2 border-[#333] focus:border-[#C9A14A] rounded-none py-4 text-white placeholder:text-gray-600"
                  maxLength={8}
                  data-testid="client-code-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-gold"
                disabled={loading}
                data-testid="client-unlock-button"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                    Verifying...
                  </div>
                ) : (
                  'Unlock Gallery'
                )}
              </Button>
            </form>

            <p className="text-gray-500 text-xs mt-6">
              Your confirmation code was sent to your email after booking.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAccessPage;
